import React, { useState } from 'react';
import { DirectionalHint, ITooltipHostStyles, Separator, Text, TooltipDelay, TooltipHost } from '@fluentui/react';
import { StringConstants } from 'app/utils/constants';
import Avatar from 'app/components/avatar/Avatar';
import { Grid, GridColumn as Column, GridRowClickEvent, GridRowProps, GridCellProps } from '@progress/kendo-react-grid';
import { useTranslation } from 'react-i18next';
import { createTheme } from '@fluentui/react/lib/Styling';
import useStore, { IStore } from 'app/store';
import './QueryTable.scss';
import tagIcon from '../../../../static/icons/tagIcon.svg';
import dataPreviewHide from '../../../../static/icons/dataPreviewHide.svg';
import dataPreviewShow from '../../../../static/icons/dataPreviewShow.svg';
import statusActive from '../../../../static/icons/statusActive.svg';
import statusClosed from '../../../../static/icons/statusClosed.svg';
import statusResolved from '../../../../static/icons/statusResolved.svg';
import statusNew from '../../../../static/icons/statusNew.svg';
import { Loader } from 'app/components/loader/Loader';
import { useId } from '@fluentui/react-hooks';
import { PaginatedListOfRequestDto, RequestDomain, RequestDto, RequestStatus, TagDto } from 'generated/request-backend';
import { IColumnLookUpProps } from 'app/models/common/post';
import { formatRequestDomain, formatRequestType } from 'app/components/helpers';
import { UserDomain } from 'app/enums/UserDomain.enum';

interface IProps {
    filterData?: RequestDto[];
    paginatedData: PaginatedListOfRequestDto;
    isRequestsLoading: boolean;
    isListBuilderLoading: boolean;
}

const QueryTable: React.FC<IProps> = ({ filterData, paginatedData, isListBuilderLoading, isRequestsLoading }) => {
    const { t: translate } = useTranslation();
    const setIsListView = useStore((state: IStore) => state.setIsListView);
    const newlyAddedRow = useStore((state: IStore) => state.newlyAddedRow);
    const [toggleDataPreview, setToggleDataPreview] = useState(false);

    const [columnsLookUpData] = useState<IColumnLookUpProps[]>([
        {
            key: 0,
            column: translate('ID'),
            isChecked: true
        },
        {
            key: 1,
            column: translate('Team'),
            isChecked: true
        },
        {
            key: 2,
            column: translate('Request Type'),
            isChecked: true
        },
        {
            key: 3,
            column: translate('Title'),
            isChecked: true
        },

        {
            key: 4,
            column: translate('Assigned To'),
            isChecked: true
        },
        {
            key: 5,
            column: translate('Status'),
            isChecked: true
        },

        {
            key: 6,
            column: translate('Tags'),
            isChecked: true
        }
    ]);

    const myTheme = createTheme({
        fonts: {
            medium: {
                fontFamily: 'Monaco, Menlo, Consolas',
                fontSize: '50px'
            }
        },
        palette: {
            neutralLighter: '#C7E0F4'
        }
    });

    const grabLink = (id: string) => {
        let link = '';
        if (id !== null && id !== undefined) {
            link = '/request-detail/' + id;
        }
        return link;
    };

    const TitleCell = (props: GridCellProps) => {
        const dataItem = props.dataItem as RequestDto;
        return (
            <td className="title-link-styles" title={dataItem.summary}>
                <a target="_blank" rel="noreferrer" href={grabLink(dataItem.unifiedTrackingId)}>
                    {dataItem.summary}
                </a>
            </td>
        );
    };

    const StatusColorHandler = (status: string) => {
        switch (status) {
            case 'New':
                return <img src={statusNew} alt="new icon" />;
            case 'Active':
                return <img src={statusActive} alt="active icon" />;
            case 'Resolved':
                return <img src={statusResolved} alt="resolved icon" />;
            case 'Closed':
                return <img src={statusClosed} alt="closed icon" />;
        }
    };

    const StatusCell = (props: GridCellProps) => {
        const dataItem = props.dataItem as RequestDto;
        return (
            <td>
                <div className="statusStyles">{StatusColorHandler(RequestStatus[dataItem?.status])}</div>
            </td>
        );
    };

    const TeamCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        return (
            <td>
                {UserDomain[dataItem?.requestTeam as number] ? (
                    <div className="assigned-to-cell">{formatRequestDomain(dataItem?.requestTeam)}</div>
                ) : (
                    <div className="assigned-to-cell">
                        <span className="assigned-to-display-name">--</span>
                    </div>
                )}
            </td>
        );
    };

    const RequestCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        return (
            <td>
                {dataItem?.requestType ? (
                    <div className="request-type-cell">{formatRequestType(dataItem?.requestType)}</div>
                ) : (
                    <div className="request-type-cell">--</div>
                )}
            </td>
        );
    };

    const getColumns = () => {
        let list: IColumnLookUpProps[] = [];
        columnsLookUpData.forEach((item) => {
            arr.every((ele) => {
                if ((ele.key === item.key && item.isChecked) || item.key === 0) {
                    list.push(ele);
                    return false;
                } else {
                    return true;
                }
            });
        });
        list.push(arr[8]);
        return list;
    };

    const hostStyles: Partial<ITooltipHostStyles> = {
        root: {
            display: 'inline-block',
            borderRadius: '13px',
            maxWidth: '60px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginLeft: '5px',
            cursor: 'pointer'
        }
    };

    const tooltipId = useId('tooltip');
    const TagsHeader = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        const handleSort = (content: TagDto[]) => {
            content.sort((a: TagDto, b: TagDto) => {
                return a.name.length - b.name.length;
            });
            return content;
        };

        return (
            <td>
                {dataItem?.tags && dataItem?.tags.length > 0 ? (
                    <div>
                        <div className="tagNameContainers">
                            <img className="tag-icon-styles" src={tagIcon} alt="" />
                            {dataItem.tags[0]?.name.length > 15 ? (
                                <TooltipHost
                                    delay={TooltipDelay.zero}
                                    content={dataItem.tags[0]?.name}
                                    id={tooltipId}
                                    directionalHint={DirectionalHint.bottomCenter}
                                    styles={hostStyles}>
                                    <Text className="tag-names-tooltip">{dataItem?.tags[0]?.name}</Text>
                                </TooltipHost>
                            ) : (
                                <Text className="tagNames">{dataItem?.tags[0]?.name}</Text>
                            )}
                            {dataItem.tags.length <= 1 ? (
                                ''
                            ) : (
                                <TooltipHost
                                    delay={TooltipDelay.zero}
                                    content={handleSort(dataItem?.tags)
                                        .slice(1)
                                        .map((i: TagDto) => (
                                            <div className="listItemStyles">{` ` + i.name}</div>
                                        ))}
                                    id={tooltipId}
                                    directionalHint={DirectionalHint.bottomCenter}
                                    styles={hostStyles}
                                    className="">
                                    <span className="tag-count-container">
                                        <Text className="tag-count">+{dataItem.tags?.length - 1}</Text>
                                    </span>
                                </TooltipHost>
                            )}
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="tagNameContainers">
                            <img className="tag-icon-styles" src={tagIcon} alt="" />
                            <div className="tagNames"> -- </div>
                        </div>
                    </div>
                )}
            </td>
        );
    };

    const colorAssignee = (displayName: string) => {
        const c = displayName.toUpperCase().charAt(0);
        switch (c) {
            case 'A':
                return '#a6e9ed';
            case 'B':
                return '#eca5d1';
            case 'C':
            case 'D':
                return '#94c8d4';
            case 'E':
            case 'F':
            case 'G':
                return '#e6bfed';
            case 'H':
            case 'I':
                return '#e0cea2';
            case 'J':
            case 'K':
                return '#edbbe7';
            case 'L':
                return '#A2CEF5';
            case 'M':
            case 'N':
                return '#9abfdc';
            case 'O':
                return '#d69ca5';
            case 'P':
            case 'Q':
            case 'R':
                return '#d69ca5';
            case 'S':
                return '#9bd9db';
            case 'T':
                return '#c8d1fa';
            case 'U':
            case 'V':
            case 'W':
                return '#d9a7e0';
            case 'X':
            case 'Y':
            case 'Z':
                return '#d2ccf8';
        }
    };

    const AssignedToCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        return (
            <td>
                {dataItem?.assignee ? (
                    <div className="assigned-to-cell">
                        <Avatar
                            userId={dataItem?.assignee?.id}
                            userName={dataItem?.assignee?.displayName}
                            color={colorAssignee(dataItem?.assignee?.displayName)}
                            imageSize={25}
                            roundedPic={true}
                        />
                        <span className="assigned-to-display-name">{dataItem?.assignee?.displayName}</span>
                    </div>
                ) : (
                    <div className="assigned-to-cell">
                        <Avatar
                            userId={'0'}
                            userName={StringConstants.UNASSIGNED}
                            color="#a3b2e8"
                            imageSize={25}
                            roundedPic={true}
                        />
                        <span className="assigned-to-display-name">Unassigned</span>
                    </div>
                )}
            </td>
        );
    };

    const arr : IColumnLookUpProps[] = [
        {
            key: 0,
            column: <Column width={115} className="fontStyles" field="unifiedTrackingId" title="ID" />
        },
        {
            key: 1,
            column: <Column width={115} field="requestTeam" title="TEAM" cell={TeamCell} />
        },
        {
            key: 2,
            column: <Column width={145} title="REQUEST TYPE" field="requestType" cell={RequestCell} className='fontStyles'/>
        },
        {
            key: 3,
            column: <Column width={500} field="Title" title="TITLE" cell={TitleCell} />
        },
        {
            key: 4,
            column: <Column width={250} field="assignedTo" title="ASSIGNED TO" cell={AssignedToCell} />
        },
        {
            key: 5,
            column: <Column width={139} field="status" title="STATUS" cell={StatusCell} />
        },
        {
            key: 6,
            column: <Column width={260} field="tags" title="TAGS" cell={TagsHeader} />
        }
    ];

    const handleDataShow = () => {
        setToggleDataPreview(false);
    };
    const handleDataHide = () => {
        setToggleDataPreview(true);
    };

    const onRowClick = (event: GridRowClickEvent) => {
        setIsListView(false);
    };

    const [columnList] = useState(getColumns());

    const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
        const dataItem = props.dataItem as RequestDto;
        const white = { backgroundColor: 'white' } as CSSStyleDeclaration;
        const blue = { backgroundColor: 'rgb(175,238,238)' } as CSSStyleDeclaration;
        const trProps: CSSStyleDeclaration = dataItem.unifiedTrackingId === newlyAddedRow ? blue : white;
        return React.cloneElement(trElement, { style: trProps }, trElement.props.children);
    };

    return (
        <div>
            <div className="dataPreviewStyles">
                <div className="seperator-bar-left">
                    <Separator theme={myTheme} />
                </div>
                {toggleDataPreview ? (
                    <img src={dataPreviewShow} onClick={handleDataShow} alt="" />
                ) : (
                    <img src={dataPreviewHide} onClick={handleDataHide} alt="" />
                )}
                <div className="seperator-bar-right">
                    <Separator theme={myTheme} />
                </div>
            </div>
            <div className="requestCountContainer">
                {paginatedData && paginatedData?.totalCount > 0 ? paginatedData.totalCount : 0} results found
            </div>
            <div id="blue" className={`table-container ${toggleDataPreview ? 'page-container' : ''}`}>
                {isRequestsLoading || isListBuilderLoading ? (
                    <div className="p-a-20">
                        <div style={{ width: '100%' }}>
                            <Loader classNames="m-b-50 height-70" />
                        </div>
                    </div>
                ) : (
                    <div className={`${toggleDataPreview ? 'hide' : 'show'} parent-grid-container`} role="main" aria-label="grid table">
                        <Grid data={filterData} sortable={true} rowRender={rowRender} onRowClick={onRowClick}>
                            {columnList?.map((item: IColumnLookUpProps) => item?.column)}
                        </Grid>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QueryTable;
